/* component-carousel.js
 * Shared card-carousel enhancement engine. Consumer-agnostic: it discovers
 * carousels and their parts through a data-attribute contract ONLY — it never
 * reads a consumer's class names.
 *
 * Public contract (consumer -> engine):
 *   [data-carousel]              root; engine autodiscovers via this attribute.
 *   [data-carousel-id]           optional per-instance id (kept as-is; not required).
 *   [data-carousel-track]        the scroll container (scrollport).
 *   [data-carousel-row]          the flex row (UL) inside the track. JS row lookup;
 *                                CSS layout hook. Gap is measured from this element.
 *   [data-carousel-slide-index]  optional pre-indexed slides. If present, server ARIA
 *                                is authoritative and the engine adds none (FC path).
 *                                If ABSENT everywhere, the engine discovers slides as
 *                                row.children, assigns indices + ARIA (CL/discovery path).
 *   [data-carousel-prev/next]    consumer-rendered nav buttons.
 *   [data-carousel-dots]         dots container. If it already holds [data-carousel-dot]
 *                                buttons, the engine binds them (pre-rendered mode). If
 *                                empty, the engine generates them (generated mode).
 *   [data-carousel-dot]          dot button (pre-rendered or generated).
 *   [data-carousel-dot-index]    dot -> slide index.
 *   [data-carousel-i18n-slide]   optional localized aria-roledescription for the
 *                                discovery branch (default 'slide'). Unused by the
 *                                pre-indexed path.
 *   [data-carousel-init]         engine-set per-element re-init guard.
 *
 * Custom properties (consumer -> shared CSS component-carousel.css):
 *   --carousel-per-view-mobile / --carousel-per-view-desktop  (defaults 2 / 4)
 *   --carousel-gap-mobile / --carousel-gap-desktop            (C17 grid-gap chain)
 *   --carousel-slide-width                                    (optional fixed-width mode)
 *
 * Two slide modes: pre-indexed (server markup authoritative, no ARIA added) and
 * discovered (indices + ARIA assigned at runtime). Two dot modes: pre-rendered
 * (bound in place) and generated (built from the discovered slide count).
 *
 * Active state is expressed with aria-selected + roving tabindex ONLY — the engine
 * toggles NO classes. Styling keys [data-carousel-dot][aria-selected="true"].
 *
 * Architecture: CSS scroll-snap is the foundational primitive (works without JS for
 * touch/scroll/keyboard arrow-key scrolling). This file enhances with prev/next
 * button navigation, dot-indicator click-to-slide, and active-slide tracking via
 * IntersectionObserver.
 *
 * Per-carousel state lives in the initCarousel(el) closure — multiple carousels on
 * one page operate independently.
 *
 * IIFE-dedup-guarded per Rule J12. A per-element data-carousel-init guard prevents
 * double-init when a host re-runs window.__KV_CAROUSEL_INIT__(scope) after swapping
 * innerHTML.
 *
 * Reduced-motion: detected once at init via matchMedia; programmatic scroll calls
 * use 'auto' instead of 'smooth' when set. Mirrors the CSS-side
 * @media (prefers-reduced-motion) override of scroll-behavior.
 *
 * Shopify customizer hot-reload: shopify:section:load re-inits any carousel inside
 * the reloaded section. Old DOM is replaced by Shopify; old listeners + IO GC with
 * their closure.
 *
 * Adopters: the products-carousel rail is the first consumer; collection-list and
 * product-recommendations are planned P2 adopters; blog-articles planned.
 */
(function () {
  'use strict';

  if (window.__BLOCKDEDUP_CAROUSEL__) {
    return;
  }
  window.__BLOCKDEDUP_CAROUSEL__ = true;

  function enhanceSlides(slides, roleDesc) {
    var total = slides.length;
    slides.forEach(function (slide, i) {
      slide.setAttribute('data-carousel-slide-index', i);
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', roleDesc);
      slide.setAttribute('aria-label', (i + 1) + ' / ' + total);
    });
  }

  function buildDots(dotsOl, slideCount) {
    dotsOl.innerHTML = '';
    for (var i = 0; i < slideCount; i++) {
      var li = document.createElement('li');
      li.className = 'carousel__dot-item';
      li.setAttribute('role', 'presentation');

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'carousel__dot';
      btn.setAttribute('data-carousel-dot', '');
      btn.setAttribute('data-carousel-dot-index', i);
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      btn.setAttribute('tabindex', i === 0 ? '0' : '-1');
      btn.setAttribute('aria-label', (i + 1) + ' / ' + slideCount);

      li.appendChild(btn);
      dotsOl.appendChild(li);
    }
  }

  function initCarousel(carouselEl) {
    if (carouselEl.dataset.carouselInit) return;
    carouselEl.dataset.carouselInit = '1';

    var trackEl = carouselEl.querySelector('[data-carousel-track]');
    var prevBtn = carouselEl.querySelector('[data-carousel-prev]');
    var nextBtn = carouselEl.querySelector('[data-carousel-next]');

    // Pre-indexed slides first (FC path — server ARIA authoritative).
    var slides = carouselEl.querySelectorAll('[data-carousel-slide-index]');

    // Row lookup: explicit hook, else the parent of the first pre-indexed slide.
    var row = carouselEl.querySelector('[data-carousel-row]') || (slides[0] && slides[0].parentElement);

    // Discovery branch (CL path): no pre-indexed slides -> derive from row.children.
    if (slides.length === 0) {
      if (!row) return;
      slides = Array.prototype.slice.call(row.children);
      var roleDesc = carouselEl.getAttribute('data-carousel-i18n-slide') || 'slide';
      enhanceSlides(slides, roleDesc);
    }

    if (!trackEl || !prevBtn || !nextBtn || slides.length === 0) {
      return;
    }

    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var scrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

    function getSlideStep() {
      var firstSlide = slides[0];
      if (!firstSlide || !row) return 0;
      var slideWidth = firstSlide.offsetWidth;
      var rowStyle = window.getComputedStyle(row);
      var gap = parseFloat(rowStyle.columnGap || rowStyle.gap) || 0;
      return slideWidth + gap;
    }

    function scrollByOneSlide(direction) {
      var step = getSlideStep();
      if (step === 0) return;
      trackEl.scrollBy({
        left: direction * step,
        behavior: scrollBehavior
      });
    }

    function scrollToSlide(idx) {
      var targetSlide = slides[idx];
      if (!targetSlide) return;
      var trackBox = trackEl.getBoundingClientRect();
      var slideBox = targetSlide.getBoundingClientRect();
      var offset = slideBox.left - trackBox.left + trackEl.scrollLeft;
      trackEl.scrollTo({ left: offset, behavior: scrollBehavior });
    }

    // Dots: bind pre-rendered buttons, or generate them from the discovered count.
    var dotsContainer = carouselEl.querySelector('[data-carousel-dots]');
    var dots = carouselEl.querySelectorAll('[data-carousel-dot]');
    if (dots.length === 0 && dotsContainer) {
      buildDots(dotsContainer, slides.length);
      dots = carouselEl.querySelectorAll('[data-carousel-dot]');
    }

    // Single uniform click-binding pass over the resolved dots (both modes).
    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var idx = parseInt(dot.getAttribute('data-carousel-dot-index'), 10);
        if (!isNaN(idx)) scrollToSlide(idx);
      });
    });

    function setActiveDot(idx) {
      if (!dots || dots.length === 0) return;
      dots.forEach(function (dot, i) {
        var isActive = i === idx;
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        dot.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    }

    function updateEdgeStates() {
      var atStart = trackEl.scrollLeft <= 1;
      var atEnd = trackEl.scrollLeft + trackEl.clientWidth >= trackEl.scrollWidth - 1;
      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd;
    }

    function handleIntersect(entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var idx = parseInt(entry.target.getAttribute('data-carousel-slide-index'), 10);
          if (!isNaN(idx)) setActiveDot(idx);
        }
      });
    }

    prevBtn.addEventListener('click', function () { scrollByOneSlide(-1); });
    nextBtn.addEventListener('click', function () { scrollByOneSlide(1); });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(handleIntersect, {
        root: trackEl,
        threshold: 0.6
      });
      slides.forEach(function (slide) { io.observe(slide); });
    }

    trackEl.addEventListener('scroll', updateEdgeStates, { passive: true });
    updateEdgeStates();

    // --- Autoplay (opt-in via data-carousel-autoplay="<seconds>"; attr absent = fully inert) ---
    var autoplaySeconds = parseInt(carouselEl.getAttribute('data-carousel-autoplay'), 10) || 0;
    if (autoplaySeconds > 0) {
      var pauseBtn = carouselEl.querySelector('[data-carousel-pause]');

      if (prefersReducedMotion) {
        // Never start; the consumer renders the button unconditionally (liquid can't read
        // motion prefs), so the engine finishes the job by hiding it.
        if (pauseBtn) pauseBtn.hidden = true;
      } else {
        var apTimer = null;
        var apStopped = false; // user pressed pause; only a play press clears it
        var apPointerOver = false;
        var apHasFocus = false;
        var apOnScreen = true;

        var apCanRun = function () {
          return !apStopped && !apPointerOver && !apHasFocus && apOnScreen;
        };

        // State attr + aria-label carry the USER-FACING toggle only: 'running' | 'stopped'.
        // Soft pauses (hover / focus / off-screen) never call this — they suspend the timer
        // silently, leaving the glyph and the label unchanged.
        var apSetState = function (state) {
          carouselEl.setAttribute('data-carousel-autoplay-state', state);
          if (pauseBtn) {
            var label = pauseBtn.getAttribute(state === 'running' ? 'data-label-pause' : 'data-label-play');
            if (label) pauseBtn.setAttribute('aria-label', label);
          }
        };

        var apAdvance = function () {
          // No-overflow guard: nothing to advance when the content fits (few-items case).
          if (trackEl.scrollWidth <= trackEl.clientWidth + 1) return;
          if (trackEl.scrollLeft + trackEl.clientWidth >= trackEl.scrollWidth - 1) {
            scrollToSlide(0);
          } else {
            scrollByOneSlide(1);
          }
        };

        var apClearTimer = function () {
          if (apTimer) { clearInterval(apTimer); apTimer = null; }
        };

        var apTick = function () {
          // Tick-level teardown: an innerHTML/section swap detaches the old root; its timer
          // reaps itself here (no unload/disconnectedCallback reliance).
          if (!carouselEl.isConnected) { apClearTimer(); return; }
          apAdvance();
        };

        // Arm / clear the TIMER ONLY — neither touches the state attr or the aria-label.
        var apArm = function () {
          if (apTimer || !apCanRun()) return;
          apTimer = setInterval(apTick, autoplaySeconds * 1000);
        };

        // Soft pauses (auto-resume): suspend the timer; state attr + label stay put.
        carouselEl.addEventListener('pointerenter', function () { apPointerOver = true; apClearTimer(); });
        carouselEl.addEventListener('pointerleave', function () { apPointerOver = false; apArm(); });
        carouselEl.addEventListener('focusin', function () { apHasFocus = true; apClearTimer(); });
        carouselEl.addEventListener('focusout', function (e) {
          if (carouselEl.contains(e.relatedTarget)) return;
          apHasFocus = false;
          apArm();
        });

        // Soft pause: root off-screen. Distinct viewport observer (root:null, 0.5) — the
        // existing active-dot IO is rooted on the track and can't see viewport visibility.
        if ('IntersectionObserver' in window) {
          var apIO = new IntersectionObserver(function (entries) {
            apOnScreen = entries[0].isIntersecting;
            if (apOnScreen) { apArm(); } else { apClearTimer(); }
          }, { threshold: 0.5 });
          apIO.observe(carouselEl);
        }

        // The pause/play button is the ONLY control that flips the user-facing state.
        // Direct prev/next/dot/drag/wheel interaction is already soft-suspended by the
        // pointer/focus guards above (they fire during any such interaction) and resumes on
        // disengage — so it needs no separate stop, and must not desync the visible glyph.
        if (pauseBtn) {
          pauseBtn.addEventListener('click', function () {
            if (apStopped) {
              // Resume. Clear the stop AND the soft guards so it runs even with the cursor
              // still on the button; the guards re-engage on the next pointerenter / focusin.
              // One immediate step, then arm — kills the click-play-nothing-happens lag.
              apStopped = false;
              apPointerOver = false;
              apHasFocus = false;
              apSetState('running');
              apAdvance();
              apArm();
            } else {
              // Stop for the rest of the page view.
              apStopped = true;
              apClearTimer();
              apSetState('stopped');
            }
          });
        }

        apSetState('running'); // initial user-facing state
        apArm();               // the IO corrects the timer if the carousel loads off-screen
      }
    }
  }

  function initAll(scopeEl) {
    var root = scopeEl || document;
    var carousels = root.querySelectorAll('[data-carousel]');
    carousels.forEach(initCarousel);
  }

  window.__KV_CAROUSEL_INIT__ = initAll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) initAll(event.target);
  });
})();
